"use client";

import React from "react";
import {
  ArrowRight, Users, Star, Clock,
  BookOpen, Mic2, Music, Brain,
} from "lucide-react";

const COURSES = [
  {
    icon: BookOpen,
    title: "Học Guitar Cơ Bản",
    desc: "Từ zero đến chơi được bài hoàn chỉnh — không cần nền tảng âm nhạc",
    tag: "Người mới",
  },
  {
    icon: Mic2,
    title: "Luyện Đệm Hát",
    desc: "Kỹ thuật picking, strumming và cách phối hợp tay trái tay phải khi hát",
    tag: "Phổ biến",
  },
  {
    icon: Music,
    title: "Luyện Solo",
    desc: "Scales, bending, vibrato và cách xây dựng câu solo có cảm xúc",
    tag: "Nâng cao",
  },
  {
    icon: Brain,
    title: "Nhạc Lý Thực Hành",
    desc: "Hiểu hợp âm, vòng hòa âm và ứng dụng ngay vào bài nhạc thực tế",
    tag: "Chuyên sâu",
  },
];

const STATS = [
  { value: "1 kèm 1", label: "Học cá nhân" },
  { value: "12 tuần", label: "Lộ trình" },
  { value: "500+",    label: "Học viên" },
];

const BENEFITS = [
  "Giáo trình cá nhân hóa theo trình độ",
  "Lịch học linh hoạt, đặt theo giờ bạn rảnh",
  "Giáo viên phản hồi kỹ thuật từng buổi",
  "Học tại Quận 12 hoặc online",
  "Học thử miễn phí buổi đầu tiên",
];

/* gold-dark token dùng nhất quán toàn component — đủ tương phản trên nền trắng */
const G = "#92702A";
const G_BG = "rgba(146,112,42,0.08)";
const G_BORDER = "rgba(146,112,42,0.22)";

const OneOnOneGuitarSection: React.FC = () => {
  return (
    <section
      aria-labelledby="oneonone-heading"
      className="w-full py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden"
      style={{
        backgroundColor: "var(--section-bg)",
        color: "var(--section-foreground)",
        fontFamily: "var(--font-roboto)",
      }}
    >
      <div className="max-w-7xl mx-auto">

        {/* ══════════════════════════════════════
            HEADER
        ══════════════════════════════════════ */}
        <header className="text-center mb-10 sm:mb-14 lg:mb-16">

          {/* Eyebrow badge */}
          <div
            className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold mb-6 sm:mb-8 border"
            style={{
              backgroundColor: G_BG,
              borderColor: G_BORDER,
              color: G,
              fontFamily: "var(--font-montserrat)",
            }}
          >
            <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Học Guitar 1 Kèm 1 – Quận 12
          </div>

          {/* ── 3-TIER HEADING ──
              Tier 1: descriptor  → xs · spaced · #999
              Tier 2: hero split  → "1 Kèm" #1a1a1a / "1" gold-dark
              Tier 3: location    → font-normal · #888
          */}
          <h2
            id="oneonone-heading"
            className="mb-5 sm:mb-7 px-1"
            style={{ fontFamily: "var(--font-montserrat)", wordBreak: "keep-all" }}
          >
            <span
              className="block text-xs sm:text-sm font-medium tracking-[0.22em] uppercase mb-2 sm:mb-3"
              style={{ color: "#999" }}
            >
              Khóa Học Guitar
            </span>

            <span className="block leading-[1.05] tracking-tight mb-2.5 sm:mb-3">
              <span
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black"
                style={{ color: "#1a1a1a" }}
              >
                1 Kèm{" "}
              </span>
              <span
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black"
                style={{ color: G }}
              >
                1
              </span>
            </span>

            <span
              className="block text-sm sm:text-base md:text-lg font-normal tracking-wide"
              style={{ color: "#888" }}
            >
              tại{" "}
              <span className="font-semibold" style={{ color: "#1a1a1a" }}>
                Quận 12, Sài Gòn
              </span>
            </span>
          </h2>

          {/* Divider */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-5 sm:mb-7">
            <div className="h-px w-10 sm:w-16 rounded-full" style={{ backgroundColor: G_BORDER }} />
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: G }} />
            <div className="h-px w-10 sm:w-16 rounded-full" style={{ backgroundColor: G_BORDER }} />
          </div>

          {/* Body copy */}
          <p
            className="text-sm sm:text-base lg:text-lg leading-relaxed max-w-xs sm:max-w-xl md:max-w-2xl mx-auto"
            style={{ color: "#666" }}
          >
            Ngoài việc cung cấp guitar cũ, Guitar Sài Gòn còn tổ chức{" "}
            <strong style={{ color: "#1a1a1a", fontWeight: 600 }}>khóa học guitar</strong>{" "}
            dành cho người mới đến nâng cao. Hình thức{" "}
            <strong style={{ color: "#1a1a1a", fontWeight: 600 }}>học 1 kèm 1</strong>{" "}
            giúp học viên tiến bộ nhanh và đúng kỹ thuật tại{" "}
            <strong style={{ color: "#1a1a1a", fontWeight: 600 }}>Sài Gòn</strong>.
          </p>
        </header>

        {/* ══════════════════════════════════════
            STATS ROW
        ══════════════════════════════════════ */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8 sm:mb-12 max-w-xs sm:max-w-sm md:max-w-lg mx-auto">
          {STATS.map(({ value, label }) => (
            <div
              key={label}
              className="flex flex-col items-center gap-0.5 sm:gap-1 py-3.5 sm:py-5 px-1.5 sm:px-3 rounded-xl sm:rounded-2xl border"
              style={{
                backgroundColor: "#fff",
                borderColor: G_BORDER,
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
              }}
            >
              <span
                className="text-lg sm:text-2xl font-black leading-none text-center"
                style={{ fontFamily: "var(--font-montserrat)", color: G }}
              >
                {value}
              </span>
              <span
                className="text-[9px] sm:text-xs font-medium text-center leading-tight mt-0.5"
                style={{ color: "#888" }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* ══════════════════════════════════════
            MAIN CARD
        ══════════════════════════════════════ */}
        <div
          className="rounded-2xl sm:rounded-3xl overflow-hidden border mb-5 sm:mb-8"
          style={{
            backgroundColor: "var(--section-card)",
            borderColor: G_BORDER,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07), 0 0 0 1px rgba(146,112,42,0.05)",
          }}
        >
          <div className="flex flex-col lg:grid lg:grid-cols-2">

            {/* ── IMAGE PANEL ── */}
            <div className="relative h-52 sm:h-72 lg:h-auto lg:min-h-[480px] overflow-hidden flex-shrink-0">
              <img
                src="https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=800&h=600&fit=crop"
                alt="Khóa học guitar 1 kèm 1 tại Quận 12"
                className="w-full h-full object-cover"
                loading="lazy"
                style={{ filter: "brightness(0.85) contrast(1.05)" }}
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.1) 48%, transparent 70%)",
                }}
              />
              <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: G }} />

              <div
                className="absolute top-3 sm:top-5 left-3 sm:left-5 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold border backdrop-blur-sm"
                style={{
                  backgroundColor: "rgba(13,13,13,0.72)",
                  borderColor: "rgba(200,169,110,0.4)",
                  color: "#C8A96E",
                  fontFamily: "var(--font-montserrat)",
                }}
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                1 Thầy – 1 Trò
              </div>

              <div className="absolute bottom-3 sm:bottom-5 left-3 sm:left-5 right-3 sm:right-5">
                <div className="flex items-center gap-0.5 sm:gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 sm:w-4 sm:h-4 fill-current" style={{ color: "#C8A96E" }} />
                  ))}
                </div>
                <p className="text-white text-xs sm:text-sm font-medium leading-snug">
                  "Học 1 kèm 1 – tiến bộ nhanh hơn tự học gấp 3 lần"
                </p>
              </div>
            </div>

            {/* ── CONTENT PANEL ── */}
            <div className="p-5 sm:p-8 lg:p-10 xl:p-12 flex flex-col justify-center">

              <p
                className="text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-4 sm:mb-5"
                style={{ color: G, fontFamily: "var(--font-montserrat)" }}
              >
                Nội dung khóa học
              </p>

              {/* Course cards — 2 col, compact on mobile */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-6 sm:mb-8">
                {COURSES.map(({ icon: Icon, title, desc, tag }) => (
                  <div
                    key={title}
                    className="rounded-xl p-3 sm:p-4 border flex flex-col gap-1.5 sm:gap-2"
                    style={{
                      backgroundColor: "var(--section-bg)",
                      borderColor: G_BORDER,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <div
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: G_BG, border: `1.5px solid ${G_BORDER}` }}
                      >
                        <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: G }} />
                      </div>
                      <span
                        className="text-[9px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full shrink-0 leading-tight"
                        style={{
                          backgroundColor: G_BG,
                          color: G,
                          fontFamily: "var(--font-montserrat)",
                          border: `1px solid ${G_BORDER}`,
                        }}
                      >
                        {tag}
                      </span>
                    </div>
                    <p
                      className="text-xs sm:text-sm font-black leading-snug"
                      style={{ fontFamily: "var(--font-montserrat)", color: "#1a1a1a" }}
                    >
                      {title}
                    </p>
                    <p className="text-[10px] sm:text-xs leading-relaxed" style={{ color: "#777" }}>
                      {desc}
                    </p>
                  </div>
                ))}
              </div>

              {/* Benefits */}
              <ul className="space-y-2 sm:space-y-2.5 mb-6 sm:mb-8">
                {BENEFITS.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 sm:gap-3">
                    <div
                      className="shrink-0 w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex items-center justify-center rounded-full text-[9px] sm:text-xs font-black"
                      style={{
                        backgroundColor: G_BG,
                        color: G,
                        border: `1.5px solid ${G_BORDER}`,
                      }}
                    >
                      ✓
                    </div>
                    <span className="text-xs sm:text-sm leading-relaxed" style={{ color: "#333" }}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA — full width on mobile */}
              <a
                href="/dang-ky-hoc-guitar"
                className="group inline-flex items-center justify-center gap-2 sm:gap-3 font-black rounded-xl transition-all duration-200 hover:opacity-90 active:scale-[0.98] w-full sm:w-auto"
                style={{
                  backgroundColor: G,
                  color: "#fff",
                  fontFamily: "var(--font-montserrat)",
                  fontSize: "0.875rem",
                  height: "52px",
                  paddingLeft: "1.75rem",
                  paddingRight: "1.75rem",
                  boxShadow: `0 4px 20px rgba(146,112,42,0.28)`,
                }}
              >
                Đăng Ký Học Thử
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>

              {/* Trust row */}
              <div
                className="mt-5 sm:mt-7 pt-4 sm:pt-6 flex items-center gap-2 sm:gap-3 border-t"
                style={{ borderColor: G_BORDER }}
              >
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" style={{ color: G }} />
                <p className="text-xs sm:text-sm" style={{ color: "#888" }}>
                  <span className="font-semibold" style={{ color: "#1a1a1a" }}>
                    Miễn phí buổi học thử
                  </span>{" "}
                  — không cần cam kết trước
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            BOTTOM NOTE
        ══════════════════════════════════════ */}
        <div
          className="rounded-2xl px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-4 border"
          style={{ backgroundColor: G_BG, borderColor: G_BORDER }}
        >
          <span className="text-xl sm:text-2xl shrink-0">🎸</span>
          <p className="text-xs sm:text-sm leading-relaxed" style={{ color: "#444" }}>
            <span className="font-bold" style={{ fontFamily: "var(--font-montserrat)", color: G }}>
              Học guitar tại Sài Gòn?{" "}
            </span>
            Guitar Sài Gòn nhận học viên tại Quận 12 và khu vực lân cận — Gò Vấp, Hóc Môn, Bình Dương. Liên hệ để được tư vấn lộ trình phù hợp.
          </p>
        </div>

      </div>
    </section>
  );
};

export default OneOnOneGuitarSection;